// absolute imports
import { useReactiveVar } from '@apollo/client';
import { useCallback, useMemo } from 'react';

// relative imports
import { cardExpiryThresholdDays } from '../../../data/app-config/appDetails';
import { paymentSourceExpiryDismissedVar } from '../../../data/apollo/cache/reactiveVars';
import { getCardExpiryAlert } from '../../billing/chargebee/paymentSourceUtilities';
import { useAccount } from '../../providers/AccountProvider';
import {
  useBilling,
  useBillingActions,
} from '../../providers/BillingProvider';

const usePaymentSourceExpiry = ({
  thresholdDays = cardExpiryThresholdDays,
} = {}) => {
  const { clientPermissions: permissions = {} } = useAccount() || {};
  const { paymentSource, loadingBilling, currentSubscription } =
    useBilling() || {};
  const { openChargebeePortal, refetchPaymentSource } =
    useBillingActions() || {};
  const dismissed = useReactiveVar(paymentSourceExpiryDismissedVar);

  const alert = useMemo(
    () => getCardExpiryAlert(paymentSource, { thresholdDays }),
    [paymentSource, thresholdDays],
  );

  const handleUpdatePayment = useCallback(() => {
    openChargebeePortal?.(
      'openSection',
      'PAYMENT_SOURCES',
      undefined,
      refetchPaymentSource,
    );
  }, [openChargebeePortal, refetchPaymentSource]);

  const handleDismiss = useCallback(() => {
    paymentSourceExpiryDismissedVar(true);
  }, []);

  const canUpdate = Boolean(permissions.billingView);
  const hasActiveSubscription = currentSubscription?.status === 'active';

  const show =
    !loadingBilling &&
    !permissions.isAdminViewingClient &&
    hasActiveSubscription &&
    alert.shouldAlert &&
    !dismissed;
    

  return {
    ...alert,
    show,
    loadingBilling,
    canUpdate,
    dismissed,
    handleUpdatePayment,
    handleDismiss,
  };
};

export default usePaymentSourceExpiry;
